import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, fmtDate, errMsg } from "@/lib/api";
import { EmptyState } from "@/components/app/EmptyState";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileOutput, Loader2, Send, Download } from "lucide-react";
import { toast } from "sonner";

const heute = () => new Date().toISOString().slice(0, 10);

const fmtZeit = (iso) => {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function AdminReihungPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [datum, setDatum] = useState(heute());
  const [tour, setTour] = useState("");
  const [generating, setGenerating] = useState(false);
  const [transferring, setTransferring] = useState(null);

  const load = useCallback(() => {
    api.get("/reihung").then((res) => setRows(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const generieren = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/reihung/generieren", { datum, tour: tour || null });
      toast.success(`${res.data.erzeugt.length} Reihungsdatei(en) erstellt.`);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Generierung fehlgeschlagen."));
    } finally {
      setGenerating(false);
    }
  };

  const uebertragen = async (id) => {
    setTransferring(id);
    try {
      const res = await api.post(`/reihung/${id}/uebertragen`);
      toast.success(res.data.meldung || "Erfolgreich an das Qualitätslabor übertragen.");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Übertragung fehlgeschlagen."));
    } finally {
      setTransferring(null);
    }
  };

  const download = async (r) => {
    try {
      const res = await api.get(`/reihung/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.dateiname;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMsg(err, "Download fehlgeschlagen."));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Reihungsdateien</h1>
        <p className="text-sm text-muted-foreground">Reihung der Milchproben je Tour generieren und an das Qualitätslabor Österreich übertragen (gem. Milch-Güteverordnung).</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Neue Reihungsdatei generieren</CardTitle>
          <CardDescription>Erzeugt je Tour eine Datei aus den Lieferungen mit Probenahme am gewählten Tag.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="reihung-datum">Datum der Probenahme</Label>
            <Input id="reihung-datum" data-testid="reihung-date-input" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className="w-[180px] bg-card" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reihung-tour">Tour (optional)</Label>
            <Input id="reihung-tour" data-testid="reihung-tour-input" placeholder="z.B. T01" value={tour} onChange={(e) => setTour(e.target.value)} className="w-[120px] bg-card" />
          </div>
          <Button data-testid="reihung-generate-button" onClick={generieren} disabled={generating}>
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileOutput className="h-4 w-4 mr-2" />}
            Generieren
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Historie</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : rows.length ? (
            <div className="overflow-x-auto">
              <Table data-testid="reihung-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Datei</TableHead>
                    <TableHead>Probenahme</TableHead>
                    <TableHead>Tour</TableHead>
                    <TableHead className="text-right">Proben</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Übertragen am</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium text-sm">{r.dateiname}</TableCell>
                      <TableCell>{fmtDate(r.datum)}</TableCell>
                      <TableCell>{r.tour}</TableCell>
                      <TableCell className="text-right font-num">{fmtNum(r.anzahl)}</TableCell>
                      <TableCell>
                        <Badge variant={r.status === "uebertragen" ? "secondary" : "outline"}>
                          {r.status === "uebertragen" ? "übertragen" : "erstellt"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtZeit(r.uebertragen_am)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button data-testid={`reihung-download-button-${r.id}`} variant="ghost" size="icon" title="Herunterladen" onClick={() => download(r)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button data-testid={`reihung-transfer-button-${r.id}`} variant="outline" size="sm" disabled={transferring === r.id} onClick={() => uebertragen(r.id)}>
                            {transferring === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                            An Labor
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState icon={FileOutput} title="Noch keine Reihungsdateien erstellt." text="Wählen Sie ein Datum mit Probenahmen und klicken Sie auf Generieren." />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
